import { JourneyCreator } from '@js/utilities/journey'
import { fieldTransformations } from '@js/utilities/fields'
import { Router } from 'vue-router'
import { shallowRef } from 'vue'

// lodash components
import indexOf from 'lodash/indexOf'
import _get from 'lodash/get'
import _set from 'lodash/set'
import camelCase from 'lodash/camelCase'

export default {
    addAvailableStep: (state, component) => {
        let stepData = component?.stepDefinition()

        if (!stepData) {
            console.error('Missing step data for component', component)
        }

        if (!(stepData instanceof Array)) {
            stepData = [stepData]
        }

        // multiple steps can use the same file
        for (const step of stepData) {
            state.allAvailableSteps[step.name] = shallowRef(component)
        }
    },
    setJourneyData: (state, payload) => {
        // only set the journey once so we don't change existing values
        if (!state.journey) {
            const journey = new JourneyCreator(payload.journey, state.allAvailableSteps, state.$t, payload.store)
            state.journey = journey.get()

            /** @type Router */
            const router = payload.router

            for (const route of journey.getStepRoutes()) {
                router.addRoute('apply', route)
            }

            if (payload.journey?.default_values) {
                for (const fieldKey in payload.journey.default_values) {
                    const fieldValue = payload.journey.default_values[fieldKey]
                    state.data[camelCase(fieldKey)] = {
                        value: fieldValue,
                        mappedKey: fieldKey,
                    }
                }
            }

            state.smallSummaryIntent = payload.journey?.small_summary
            state.has.smallSummary = state.smallSummaryIntent == 'always'

            state.applyRoutesLoaded = true
        }

        return null
    },
    addFieldDefinitionLoadedEvent: (state, payload) => {
        state.fieldLoadedEvents.push({
            name: payload.name,
            function: payload.function,
            runOnce: payload.runOnce,
        })
    },
    // explicitly set a data field or a custom vuex field
    setDataFieldValue: (state, payload) => {
        const path = (payload.key || 'data') + '.' + payload.name
        const field = _get(state, path, null)

        let value = fieldTransformations(field, payload.value)
        if (field && field.hasOwnProperty('value')) {
            field.value = value
        } else {
            _set(state, path + '.value', value)
        }
    },
    setInternal: (state, payload) => {
        state.internal[payload.name] = payload.value
    },
    addDynamicFields: (state, payload) => {
        state.data[payload] = { value: null }
    },
    addResultReference: (state, payload) => {
        if (indexOf(state.resultReferences, payload) < 0) {
            state.resultReferences.push(payload)
        }
    },
    setLoanRanges: (state, payload) => {
        state.loanAmountRange.min = payload.min
        state.loanAmountRange.max = payload.max

        state.fieldLoadedEvents.push({
            name: 'loanAmount',
            function: field => {
                field.validationRules.amountBetween = state.loanAmountRange
            },
            runOnce: true,
        })
    },
    addFieldValidationMessages: state => {
        state.fieldLoadedEvents.push({
            name: 'loanAmount',
            function: field => {
                field.validationRules.amountBetween.errorMessage = state.$t(
                    'form.fields.loanAmount.amountBetweenErrorMessage',
                )
            },
            runOnce: true,
        })
    },
    updateMappingKey: (state, payload) => {
        try {
            if (!payload.data) {
                console.warn('No data name specified')
                return
            }

            if (!payload.mappedKey) {
                console.warn('No mappedKey specified')
                return
            }

            if (state.data?.[payload.data]) {
                state.data[payload.data].mappedKey = payload.mappedKey
            } else {
                console.warn('Field/data name not found')
            }
        } catch (e) {
            console.error(`Can't update ${payload.field || payload.data} mappedKey`)
        }
    },
    updateMetaData: (state, payload) => {
        try {
            if (!payload.meta) {
                console.warn('No meta name specified')
                return
            }

            if (!payload.value) {
                console.warn('No meta value specified')
                return
            }

            if (!payload.route) {
                console.warn('No route specified')
                return
            }

            if (!payload.route?.meta?.[payload.meta]) {
                console.warn('Meta name not found')
                return
            }

            payload.route.meta[payload.meta] = payload.value
        } catch (e) {
            console.error(`Can't update meta data`)
        }
    },
}
